<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'

interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
} = useRanking(props.columnId, props.config)
</script>

<template>
  <div class="page h-100dvh overflow-scroll">
    <BasicNavbar class="top-2 color-n-text-2" />

    <div class="title h-60px">
      {{ title || '' }}
    </div>

    <div ref="infiniteRef" class="container rounded">
      <div v-for="(book, index) in collections" :key="index">
        <router-link
          :to="{ name: 'book', params: { contentId: book.id } }" replace
          class="flex items-center px-3 py-2"
        >
          <div class="book-cover-wrapper w-66px h-88px shrink-0 relative">
            <BizBookCover
              :id="uuid()"
              :src="book.contentCoverUrl || ''"
              :extra="{
                module_name: books.showName,
                module_id: books.id,
                module_type: books.columnType,
                author: book.authorInfo.author,
                ...book,
              }"
              class="w-full h-full border-rd-8px overflow-hidden"
            />
            <div class="medal">
              <img v-if="index === 0" :src="config.resources.goldMedal">
              <img v-else-if="index === 1" :src="config.resources.silverMedal">
              <img v-else-if="index === 2" :src="config.resources.bronze">
              <img v-else :src="config.resources.medal">
              <div class="medal-num">
                {{ index + 1 }}
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-hidden ml-3">
            <div class="font-500 truncate">
              {{ book.contentName }}
            </div>
            <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-2" />
            <div class="text-n-text-3 text-12px line-clamp-2 mt-2">
              {{ book.introduce }}
            </div>
          </div>
        </router-link>
      </div>

      <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
        {{ config.texts.noMore }}
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page {
  background: url('/images/novelarc/ranking/ranking-bg.webp') no-repeat top left;
  background-size: 100% auto;
  padding: 0 10px;
}

.title {
  font-weight: 500;
  font-size: 18px;
  color: #32353A;
  line-height: 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.book-cover-wrapper {
  position: relative;
}

.medal {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 10;
}

.medal img {
  width: 20px;
  height: 20px;
}

.medal-num {
  position: absolute;
  top: 1.5px;
  left: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
}

.container {
  overflow-y: scroll;
  height: calc(100dvh - 70px);
  padding-top: 10px;
}
</style>
